/**
 * Multi-pair selection for PDP.
 * Pair quantity (1/2/3) is a custom UI control — not a Shopify variant.
 * Colour / Size stay as native options. When qty > 1, customer must
 * confirm a variant into each pair slot before Add to Cart is enabled.
 * Cart add uses /cart/add.js with multiple items.
 */
;(function () {
  const SELECTORS = {
    pairOption: '.pair-option',
    qtyInput: '#selected-pair-qty',
    variantSelects: 'variant-selects',
    selectedVariantJson: 'variant-selects [data-selected-variant]',
    productForm: 'product-form',
    submitButton: 'product-form [type="submit"]',
    submitButtonText: 'product-form [type="submit"] span',
    panel: '[data-multi-pair-panel]',
    slots: '[data-multi-pair-slots]',
    hint: '[data-multi-pair-hint]',
    saveBtn: '[data-multi-pair-save]',
    clearBtn: '[data-multi-pair-clear]'
  }

  class MultiPairController {
    constructor() {
      this.requiredQty = 1
      this.slots = []
      this.activeIndex = 0
      this.bound = false
      this.init()
    }

    init() {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.bind())
      } else {
        this.bind()
      }
    }

    bind() {
      this.panel = document.querySelector(SELECTORS.panel)
      this.slotsEl = document.querySelector(SELECTORS.slots)
      this.hintEl = document.querySelector(SELECTORS.hint)
      this.saveBtn = document.querySelector(SELECTORS.saveBtn)
      this.clearBtn = document.querySelector(SELECTORS.clearBtn)
      this.qtyInput = document.querySelector(SELECTORS.qtyInput)

      if (!this.panel || !this.slotsEl) return

      this.requiredQty = parseInt(this.qtyInput?.value || '1', 10) || 1
      this.resetSlots(this.requiredQty)
      this.render()

      document.querySelectorAll(SELECTORS.pairOption).forEach((option) => {
        option.addEventListener('click', () => {
          const qty = parseInt(option.dataset.qty, 10) || 1
          this.setRequiredQty(qty)
        })
      })

      this.saveBtn?.addEventListener('click', () => this.saveActiveSlot())
      this.clearBtn?.addEventListener('click', () => this.clearAll())

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.variantChange, () => {
          this.syncSubmitState()
        })
      }

      // Intercept ATC for multi-item add
      const productFormEl = document.querySelector(SELECTORS.productForm)
      if (productFormEl && !productFormEl.dataset.multiPairBound) {
        productFormEl.dataset.multiPairBound = 'true'
        const form = productFormEl.querySelector('form')
        form?.addEventListener(
          'submit',
          (evt) => {
            if (this.requiredQty <= 1) return
            evt.preventDefault()
            evt.stopImmediatePropagation()
            this.addMultipleToCart(evt)
          },
          true
        )
      }

      this.bound = true
      this.syncSubmitState()
      window.MultiPair = this
    }

    setRequiredQty(qty) {
      const next = Math.max(1, Math.min(3, qty))
      if (next === this.requiredQty && this.slots.length === next) {
        this.render()
        this.syncSubmitState()
        return
      }
      this.requiredQty = next
      if (this.qtyInput) this.qtyInput.value = String(next)
      this.resetSlots(next)
      this.render()
      this.syncSubmitState()
    }

    resetSlots(count) {
      this.slots = Array.from({ length: count }, () => null)
      this.activeIndex = 0
    }

    getCurrentVariant() {
      const jsonEl = document.querySelector(SELECTORS.selectedVariantJson)
      if (jsonEl?.textContent?.trim()) {
        try {
          return JSON.parse(jsonEl.textContent)
        } catch (e) {
          /* fall through */
        }
      }

      const idInput = document.querySelector(
        'product-form form input[name="id"]'
      )
      const id = idInput?.value
      if (!id) return null
      return { id: Number(id), title: '', available: true }
    }

    saveActiveSlot() {
      if (this.requiredQty <= 1) return

      const variant = this.getCurrentVariant()
      if (!variant?.id) {
        this.setHint('Select colour and size first.')
        return
      }
      if (variant.available === false) {
        this.setHint('That variant is unavailable. Pick another option.')
        return
      }

      this.slots[this.activeIndex] = {
        id: Number(variant.id),
        title: variant.title || variant.name || `Variant ${variant.id}`,
        options: variant.options || []
      }

      const nextEmpty = this.slots.findIndex((s) => !s)
      this.activeIndex = nextEmpty === -1 ? this.activeIndex : nextEmpty
      this.render()
      this.syncSubmitState()
    }

    clearSlot(index) {
      this.slots[index] = null
      this.activeIndex = index
      this.render()
      this.syncSubmitState()
    }

    clearAll() {
      this.resetSlots(this.requiredQty)
      this.render()
      this.syncSubmitState()
    }

    isComplete() {
      if (this.requiredQty <= 1) return true
      return (
        this.slots.length === this.requiredQty &&
        this.slots.every((s) => s && s.id)
      )
    }

    filledCount() {
      return this.slots.filter((s) => s && s.id).length
    }

    setHint(text) {
      if (this.hintEl) this.hintEl.textContent = text
    }

    render() {
      const multi = this.requiredQty > 1
      this.panel.hidden = !multi
      this.panel.setAttribute('aria-hidden', multi ? 'false' : 'true')

      if (!multi) {
        this.setHint('')
        return
      }

      this.slotsEl.innerHTML = this.slots
        .map((slot, index) => {
          const filled = !!(slot && slot.id)
          const active = index === this.activeIndex
          const label = filled
            ? slot.title
            : active
              ? 'Selecting…'
              : 'Not selected'
          return `
            <div class="multi-pair-slot${filled ? ' is-filled' : ''}${active ? ' is-active' : ''}" data-slot-index="${index}">
              <button type="button" class="multi-pair-slot__main" data-activate-slot="${index}">
                <span class="multi-pair-slot__index">Pair ${index + 1}</span>
                <span class="multi-pair-slot__label">${this.escapeHtml(label)}</span>
              </button>
              ${
                filled
                  ? `<button type="button" class="multi-pair-slot__clear" data-clear-slot="${index}" aria-label="Clear pair ${index + 1}">×</button>`
                  : ''
              }
            </div>
          `
        })
        .join('')

      this.slotsEl.querySelectorAll('[data-activate-slot]').forEach((btn) => {
        btn.addEventListener('click', () => {
          this.activeIndex = parseInt(btn.getAttribute('data-activate-slot'), 10)
          this.render()
        })
      })
      this.slotsEl.querySelectorAll('[data-clear-slot]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation()
          this.clearSlot(parseInt(btn.getAttribute('data-clear-slot'), 10))
        })
      })

      const filled = this.filledCount()
      if (this.isComplete()) {
        this.setHint(`All ${this.requiredQty} pairs selected. You can add them to the basket.`)
      } else {
        this.setHint(
          `Select colour & size for Pair ${this.activeIndex + 1}, then tap “Save this pair” (${filled}/${this.requiredQty} saved).`
        )
      }
    }

    escapeHtml(str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
    }

    syncSubmitState() {
      const submit = document.querySelector(SELECTORS.submitButton)
      const submitText = document.querySelector(SELECTORS.submitButtonText)
      if (!submit) return

      if (this.requiredQty <= 1) {
        if (submit.dataset.multiPairDisabled === 'true') {
          delete submit.dataset.multiPairDisabled
          const variant = this.getCurrentVariant()
          if (variant?.available !== false) {
            submit.removeAttribute('disabled')
            submit.removeAttribute('aria-disabled')
            if (submitText && window.variantStrings?.addToCart) {
              submitText.textContent = window.variantStrings.addToCart
            }
          }
        }
        return
      }

      if (!this.isComplete()) {
        submit.setAttribute('disabled', 'disabled')
        submit.setAttribute('aria-disabled', 'true')
        submit.dataset.multiPairDisabled = 'true'
        if (submitText) {
          const remaining = this.requiredQty - this.filledCount()
          submitText.textContent =
            remaining === this.requiredQty
              ? `Select ${this.requiredQty} pairs`
              : `Select ${remaining} more pair${remaining > 1 ? 's' : ''}`
        }
      } else {
        submit.removeAttribute('disabled')
        submit.removeAttribute('aria-disabled')
        delete submit.dataset.multiPairDisabled
        if (submitText && window.variantStrings?.addToCart) {
          submitText.textContent = window.variantStrings.addToCart
        }
      }
    }

    async addMultipleToCart(evt) {
      const productForm = document.querySelector(SELECTORS.productForm)
      const submit = document.querySelector(SELECTORS.submitButton)
      const spinner = productForm?.querySelector('.loading__spinner')

      if (!this.isComplete()) {
        this.syncSubmitState()
        return
      }

      submit?.setAttribute('aria-disabled', 'true')
      submit?.classList.add('loading')
      spinner?.classList.remove('hidden')

      const activePair = document.querySelector('.pair-option.active')
      const percentOff = parseFloat(activePair?.getAttribute('percentoff') || '0') || 0

      const items = this.slots.map((slot, index) => {
        const props = {
          Bundle: `${this.requiredQty} pairs`,
          'Pair #': String(index + 1)
        }
        if (percentOff > 0) props['Bundle save'] = `${percentOff}%`
        return {
          id: slot.id,
          quantity: 1,
          properties: props
        }
      })

      const cart =
        document.querySelector('cart-notification') ||
        document.querySelector('cart-drawer')

      try {
        const addUrl = `${window.routes?.cart_add_url || '/cart/add'}.js`.replace(
          /\.js\.js$/,
          '.js'
        )
        const response = await fetch(addUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items })
        })
        const data = await response.json()

        if (data.status || data.message === 'Cart Error' || !response.ok) {
          const message =
            data.description || data.message || 'Could not add pairs to cart.'
          if (typeof productForm?.handleErrorMessage === 'function') {
            productForm.handleErrorMessage(message)
          } else {
            alert(message)
          }
          return
        }

        if (!cart) {
          window.location = window.routes?.cart_url || '/cart'
          return
        }

        // /cart/add.js (multi items) does not return section HTML — fetch it.
        const sectionIds = cart
          .getSectionsToRender()
          .map((section) => section.id)
          .join(',')
        const sectionsResponse = await fetch(
          `${window.location.pathname}?sections=${sectionIds}`
        )
        const sections = await sectionsResponse.json()
        const firstItem = Array.isArray(data) ? data[0] : data

        const payload = {
          ...firstItem,
          sections
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'multi-pair',
            cartData: payload
          })
        }

        cart.renderContents(payload)
        if (cart.classList.contains('is-empty')) {
          cart.classList.remove('is-empty')
        }
      } catch (error) {
        console.error('Multi-pair add failed', error)
      } finally {
        submit?.classList.remove('loading')
        submit?.removeAttribute('aria-disabled')
        spinner?.classList.add('hidden')
        this.syncSubmitState()
      }
    }
  }

  new MultiPairController()
})()
